import dagre from '@dagrejs/dagre';
import { type Edge, type Node, Position } from '@xyflow/react';

import { ViewEdge, ViewNode } from '../components/types.js';

import type { StepNodeData } from './nodes.tsx';

const NODE_WIDTH = 250;
const NODE_HEIGHT = 104;

/**
 * Lay out the abstract graph (nodes/edges) received from the backend using
 * dagre, and map it to React Flow nodes/edges. Layout is a view concern, so it
 * stays on the frontend.
 */
export function layoutGraph(
  viewNodes: ViewNode[],
  viewEdges: ViewEdge[],
): { nodes: Node<StepNodeData>[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 40, ranksep: 60 });

  for (const n of viewNodes) {
    g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }
  for (const e of viewEdges) {
    g.setEdge(e.source, e.target);
  }
  dagre.layout(g);

  const nodes: Node<StepNodeData>[] = viewNodes.map((n) => {
    const { x, y } = g.node(n.id);
    return {
      id: n.id,
      type: 'step',
      position: { x: x - NODE_WIDTH / 2, y: y - NODE_HEIGHT / 2 },
      data: {
        label: n.label,
        kind: n.kind,
        active: n.active,
        childWorkflowId: n.childWorkflowId,
      },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    };
  });

  const edges: Edge[] = viewEdges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    label: e.label,
    animated: e.kind === 'changes',
    className: e.kind === 'changes' ? 'edge-changes' : 'edge-then',
    labelStyle: { fontSize: 11 },
  }));

  return { nodes, edges };
}
